import { enqueueTask } from 'ember-concurrency';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
} from '../../../utils/tuition-assistance/fields.ts';
import { pluralize } from '@warp-drive/utilities/string';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import AttachmentList from './upload-attachments/attachment-list.gts';
import TuitionAssistanceFormsUploadAttachmentsUploader from './upload-attachments/uploader.gts';
import type DigitalAssetsService from '../../../services/digital-assets.ts';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type TasCourseModel from '../../../models/tas-course.ts';
import type TasProgramInstanceModel from '../../../models/tas-program-instance.ts';
import type TasProgramTemplateModel from '../../../models/tas-program-template.ts';
import type TasAssetModel from '../../../models/tas-asset.ts';

export interface UploadAttachmentsSignature {
  Args: {
    attachable: TasProgramInstanceModel | TasCourseModel | TasApplicationModel;
    attachableType: string;
    programTemplate: TasProgramTemplateModel;
    classification: string;
    uploadRequired?: boolean;
    disabled?: boolean;
    useDropzone?: boolean;
    iconOnlyUpload?: boolean;
    hideInstructions?: boolean;
    hideAttachments?: boolean;
    instructions?: string;
    items?: string[];
    attachmentListItems?: TasAssetModel[];
    didUploadFile?: () => void;
  };
  Element: HTMLDivElement;
}

export default class UploadAttachmentsComponent extends Component<UploadAttachmentsSignature> {
  /*
  COURSE_APPROVAL_DOCUMENTS - course approval
  COMPLETION_DOCUMENTS - course evidence
  PROGRAM_APPROVAL_DOCUMENTS - program approval
  */
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;

  get instructionsField() {
    return `${this.args.classification}_STRING`;
  }

  get instructions() {
    return this.args.instructions
      ? this.args.instructions
      : // @ts-expect-error: not sure about this one
        getSingleValueForTasField(this.instructionsField, this.args.programTemplate?.fields) || '';
  }

  get attachmentItemsField() {
    return `${this.args.classification}_ATTACHMENT_FIELD`;
  }

  get attachmentItems() {
    return this.args.items
      ? this.args.items
      : // @ts-expect-error: not sure about this one
        getAllValuesForTasField(this.attachmentItemsField, this.args.programTemplate?.fields) || [];
  }

  get assetsForAttachmentList() {
    return this.args.attachmentListItems ?? this.args.attachable?.tasAssets ?? [];
  }

  didUploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);

    asset.classification = this.args.classification;

    const attachable = this.args.attachable;
    const relationshipKey = pluralize(this.args.attachableType);

    if (attachable.isNew) {
      // @ts-expect-error: Dynamic relationship access on polymorphic model
      if (attachable.tasApplication?.isNew) {
        // @ts-expect-error: Dynamic relationship access on polymorphic model
        await attachable.tasApplication.save();
      }
      // @ts-expect-error: Dynamic relationship access on polymorphic model
      if (attachable.tasProgramInstance?.isNew) {
        // @ts-expect-error: Dynamic relationship access on polymorphic model
        await attachable.tasProgramInstance.save();
      }
      await attachable.save();
    }
    // @ts-expect-error: Dynamic relationship key access on polymorphic model
    asset[relationshipKey].push(attachable);
    // @ts-expect-error: Dynamic relationship access on polymorphic model
    attachable.tasAssets.push(asset);

    await asset.save();
    await attachable.save();

    if (this.args.didUploadFile) {
      this.args.didUploadFile();
    }
  });

  <template>
    <div class="flex flex-col w-full" ...attributes>
      {{#unless @hideInstructions}}
        <p class="font-semibold">
          {{! @glint-expect-error: connected to the above ts-expect-error in instructions}}
          {{this.instructions}}
          {{#if @uploadRequired}}
            <span class="text-error-400">*</span>
          {{/if}}
        </p>

        {{#if this.attachmentItems.length}}
          <ul class="font-medium text-tio-gray-700 list-disc list-inside py-2">
            {{#each this.attachmentItems as |item|}}
              {{! @glint-expect-error: connected to the above ts-expect-error in attachmentItems}}
              <li class="px-4">{{item}}</li>
            {{/each}}
          </ul>
        {{/if}}
      {{/unless}}

      <TuitionAssistanceFormsUploadAttachmentsUploader
        @didUploadFile={{this.didUploadFile.perform}}
        @isProcessing={{this.didUploadFile.isRunning}}
        @disabled={{@disabled}}
        @useDropzone={{@useDropzone}}
        @iconOnlyUpload={{@iconOnlyUpload}}
      />
      {{#unless @hideAttachments}}
        <AttachmentList @assets={{this.assetsForAttachmentList}} @disabled={{@disabled}} />
      {{/unless}}
    </div>
  </template>
}
